// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The switch matrix and the coil bus (M2-24).
//
// This is the wiring loom. The simulation emits events that say a device was touched; the rule
// engine consumes switch closures by name; the two had no connection at all until this file,
// which is why NEON NOVA's ninety-six rules could be authored, validated and loaded without a
// single one of them ever firing.
//
// It is the switch-and-coil boundary of a real machine (04 §9) and it runs in both directions:
//
//   physics → `EV_SWITCH_CLOSED` on device 7 → switch `pulsar2` → the rules
//   rules   → coil `scoopEject` → device `scoopEventHorizon` → the physics
//
// **Three things here are load-bearing.**
//
//  1. **Events are consumed in the order the simulation produced them**, which is device index
//     then ball index (see `events.ts`). Reordering here would make a replay's score depend on
//     the wiring, not on the play.
//  2. **A device with no switch raises nothing.** A slingshot the author never wired is
//     mechanically live and electrically silent, exactly as it would be on a real machine.
//  3. **A coil is pulsed, enabled or disabled** — those are the three things a solenoid driver
//     board can do, and the difference matters: a kickback that is *armed* catches the ball
//     and one that is not lets it drain. Modelling only "pulse" would mean an unlit outlane
//     had to hold the ball and wait to be told, which is a trap.

import {
  EV_BANK_COMPLETE,
  EV_SPINNER_REVOLUTION,
  EV_SWITCH_CLOSED,
  EV_TARGET_DOWN,
} from '../sim/events'
import type { SimEvent } from '../sim/events'
import type { World } from '../sim/World'
import { CaptiveBall, DropTargetBank, Kicker, Magnet, PopBumper } from '../sim/devices'
import type { Device } from '../sim/devices'
import type { RuleEffect, RuleEngine } from '../rules/RuleEngine'
import type { MachineDefinition } from '../machine/types'

/** What a `coil` action asked for. Matches `RuleEngine`'s encoding. */
export const COIL_PULSE = 0
export const COIL_ENABLE = 1
export const COIL_DISABLE = 2

export interface SwitchMatrixOptions {
  /** Device id → simulation device, from `LoadedMachine.devices`. */
  readonly devices: ReadonlyMap<string, Device>
}

export class SwitchMatrix {
  /** Device index → switch id, for events the device raises directly. */
  private readonly switchOfDevice = new Map<number, string>()
  /** Device index → the virtual switch its bank-complete raises. */
  private readonly bankSwitch = new Map<number, string>()
  /** Coil id → the devices that coil drives. A coil may drive more than one. */
  private readonly coilTargets = new Map<string, Device[]>()
  /** Every switch id the definition declares, so an unknown one is a bug rather than a no-op. */
  private readonly known: ReadonlySet<string>
  /** Devices by id, for the pulses ball search asks for by device rather than by coil. */
  private readonly byId: ReadonlyMap<string, Device>

  /** Switch ids closed on the step just pumped. Diagnostics and tests read this. */
  readonly closedThisStep: string[] = []

  constructor(definition: MachineDefinition, options: SwitchMatrixOptions) {
    this.known = new Set((definition.switches ?? []).map((s) => s.id))
    this.byId = options.devices

    for (const s of definition.switches ?? []) {
      if (s.device === undefined) continue
      const device = options.devices.get(s.device)
      if (device === undefined) continue
      this.switchOfDevice.set(device.index, s.id)
    }

    // A bank has no switch of its own in the usual sense — the interesting event is that all
    // three targets are down. NEON NOVA declares that as the virtual switch `ionBankComplete`,
    // named by the bank's own id plus `Complete`, which is the one naming convention here.
    for (const d of definition.devices) {
      if (d.kind !== 'dropTargetBank') continue
      const device = options.devices.get(d.id)
      if (device === undefined) continue
      const virtualId = `${d.id}Complete`
      if (this.known.has(virtualId)) this.bankSwitch.set(device.index, virtualId)
    }

    for (const d of definition.devices) {
      if (d.coil === undefined) continue
      const device = options.devices.get(d.id)
      if (device === undefined) continue
      const list = this.coilTargets.get(d.coil)
      if (list) list.push(device)
      else this.coilTargets.set(d.coil, [device])
    }
  }

  /** Does the definition declare this switch? Used by the host before closing a virtual one. */
  has(id: string): boolean {
    return this.known.has(id)
  }

  /**
   * Drain the simulation's event queue into the engine as switch closures.
   *
   * Called once per step, after the physics and before `RuleEngine.advance`, so a rule sees
   * the switches from the step it is resolving rather than the one before.
   */
  pump(world: World, engine: RuleEngine): void {
    this.closedThisStep.length = 0
    const events = world.events.drain()
    for (let i = 0; i < events.length; i++) {
      const id = this.switchFor(events[i]!)
      if (id === undefined) continue
      engine.closeSwitch(id)
      this.closedThisStep.push(id)
    }
  }

  private switchFor(e: SimEvent): string | undefined {
    if (e.type === EV_BANK_COMPLETE) return this.bankSwitch.get(e.device)
    // A spinner reports revolutions, not closures; a drop target reports both the hit that
    // knocked it over and the fact that it went down. Scoring wants one closure per event in
    // both cases, and `EV_TARGET_DOWN` is the one that cannot fire twice for one shot.
    if (e.type === EV_SPINNER_REVOLUTION || e.type === EV_TARGET_DOWN) {
      return this.switchOfDevice.get(e.device)
    }
    if (e.type !== EV_SWITCH_CLOSED) return undefined
    // A drop target's own `EV_SWITCH_CLOSED` is the impact, which also produced a TARGET_DOWN;
    // taking both would score every target twice.
    return this.switchOfDevice.get(e.device)
  }

  /**
   * Apply one coil effect to the machinery.
   *
   * Returns false when nothing is wired to the coil, which the host reports rather than
   * swallows: a rule firing a coil that drives nothing is a rulesheet bug the author can fix.
   */
  fire(world: World, effect: RuleEffect): boolean {
    const targets = this.coilTargets.get(effect.id)
    if (targets === undefined || targets.length === 0) return false
    for (const device of targets) {
      if (effect.value === COIL_ENABLE) {
        device.enabled = true
        if (device instanceof Magnet) device.on = true
        continue
      }
      if (effect.value === COIL_DISABLE) {
        device.enabled = false
        if (device instanceof Magnet) device.on = false
        continue
      }
      this.pulse(world, device)
    }
    return true
  }

  /**
   * Pulse one device by its id.
   *
   * Ball search asks for devices, not coils — it is pulsing whatever is *near the ball*, and
   * proximity is a property of the mechanism rather than of the wiring. Kept separate from
   * `fire` for that reason.
   */
  pulseDevice(world: World, deviceId: string): void {
    const device = this.byId.get(deviceId)
    if (device !== undefined) this.pulse(world, device)
  }

  /** What a pulse means depends on the mechanism, and only the mechanism knows. */
  private pulse(world: World, device: Device): void {
    if (device instanceof Kicker) {
      device.eject(world)
      return
    }
    if (device instanceof PopBumper) {
      device.fire(world)
      return
    }
    if (device instanceof DropTargetBank) {
      device.raiseAll(world)
      return
    }
    if (device instanceof Magnet) {
      // A pulsed magnet is a grab-and-release; `on` is what `enable`/`disable` control, so a
      // pulse here is the momentary version of the same thing.
      device.on = true
      return
    }
    if (device instanceof CaptiveBall) {
      device.reset()
    }
  }

  /** Every device a coil drives, so a host can report an unwired coil by name. */
  targetsOf(coil: string): readonly Device[] {
    return this.coilTargets.get(coil) ?? []
  }
}
